import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../contexts/AuthContext";
import { useContext, useEffect, useRef, useState } from "react";
import { fetchRecommendedJobs } from "../../../services/users.service";
import { useQuery } from "react-query";
import RecommendationJobs from "./RecommendationJobs";

// Add type declaration for the custom element
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "jobads-job-board": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        keywords?: string;
        location?: string;
        apiKey: string;
        locale?: string;
        selectedSearchResultId?: string;
      };
    }
  }
}

interface Job {
  id: string;
  title: string;
  company: string;
  logoUrl: string;
  jobUrl: string;
  jobPublishedAt: string;
  isPublished: boolean;
  publishedAt: string | null;
}

export function Jobs() {
  const { user, configurations } = useContext(AuthContext);
  const { t } = useTranslation("jobs");
  const jobBoardRef = useRef<HTMLElement>(null);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [recommendedJobs, setRecommendedJobs] = useState<Job[]>([]);
  const shouldRenderCoverLetter = configurations?.includesCoverletter;
  const jobRecommendationsKey = user?.userDetails?.discipline;
  const jobRecommendations = jobRecommendationsKey
    ? t(`disciplines.${jobRecommendationsKey}`)
    : "";
  const jobTitleParam =
    typeof jobRecommendations === "string"
      ? (jobRecommendations as string).replace(/\s/g, "-")
      : "";
  const locale = user?.locale || "de";

  const jobBoardLocation = user?.userDetails?.jobSearchCity || "Berlin";

  const { data: jobsData, isLoading } = useQuery(
    ["recommendedJobs"],
    () => fetchRecommendedJobs(),
    {
      onSuccess: (data) => {
        if (data && Array.isArray(data) && data.length > 0) {
          setRecommendedJobs(data);
          setSelectedJobId(data[0]?.id);
        }
      },
      onError: (error: any) => {
        console.error("Error loading recommended jobs:", error);
      },
    }
  );

  const handleJobSelect = (jobId: string) => {
    setSelectedJobId(jobId);
  };

  // Effect to add custom buttons to the job board
  useEffect(() => {
    if (!shouldRenderCoverLetter) {
      console.log(
        "[Jobs] Cover letter feature is disabled, skipping button setup"
      );
      return;
    }

    if (!jobBoardRef.current) {
      console.log("[Jobs] Job board ref not available yet");
      return;
    }
    console.log("[Jobs] Initializing job board buttons effect");
    let isComponentMounted = true;

    // Function to extract job data from the current view
    const extractJobData = (container: Element | null) => {
      if (!container) return null;

      // Try to find job title (big bold text)
      const title =
        container
          .querySelector('h1, h2, h3, [class*="title"]')
          ?.textContent?.trim() || "Unknown Job";

      // Try to find company name
      const company =
        container.querySelector('[class*="company"]')?.textContent?.trim() ||
        "Unknown Company";

      // Simplified approach to get description
      let description = "";

      // Find the element containing "Details zum Jobangebot"
      const allElements = container.querySelectorAll("*");

      for (let i = 0; i < allElements.length; i++) {
        const element = allElements[i];

        // Check if this element has the title text
        if (element.textContent?.includes("Details zum Jobangebot")) {
          // Get the parent element that contains both the title and description
          const parent =
            element.closest("div, section, article") || element.parentElement;

          if (parent) {
            // Get all text content from this parent, excluding the title itself
            const fullText = parent.textContent || "";
            const titleIndex = fullText.indexOf("Details zum Jobangebot");

            if (titleIndex !== -1) {
              // Extract everything after the title
              description = fullText
                .substring(titleIndex + "Details zum Jobangebot".length)
                .trim();
              break;
            }
          }
        }
      }
      return { title, company, description };
    };

    // Function to find apply buttons in shadow DOM elements
    const findApplyButtons = () => {
      console.log("[Jobs] Looking for apply buttons in shadow DOM");
      // Get all elements with shadow roots
      const allElements = document.querySelectorAll("*");
      const shadowElements = Array.from(allElements).filter(
        (el) => el.shadowRoot
      );

      console.log(
        `[Jobs] Found ${shadowElements.length} elements with shadow roots`
      );

      let applyButtonsFound: Element[] = [];

      shadowElements.forEach((el) => {
        if (el.shadowRoot) {
          // Look for application buttons
          const potentialApplyButtons = Array.from(
            el.shadowRoot.querySelectorAll(
              'button, .button, a[href], [role="button"]'
            )
          ).filter((button) => {
            const text = button.textContent?.toLowerCase() || "";
            return (
              text.includes("jetzt bewerben") || text.includes("apply now")
            );
          });

          if (potentialApplyButtons.length > 0) {
            console.log(
              `[Jobs] Found ${potentialApplyButtons.length} apply buttons in shadow element:`,
              el
            );
          }

          applyButtonsFound = [...applyButtonsFound, ...potentialApplyButtons];
        }
      });

      console.log(
        `[Jobs] Total apply buttons found: ${applyButtonsFound.length}`
      );
      return applyButtonsFound;
    };

    // Function to check if a button already has our custom buttons
    const hasCustomButtons = (button: Element): boolean => {
      if (!button.parentNode) return false;

      // Check direct siblings
      const nextSibling = button.nextElementSibling;
      if (nextSibling && nextSibling.classList.contains("custom-job-buttons")) {
        return true;
      }

      // Check if parent has our buttons
      const parent = button.parentNode as Element;
      return !!parent.querySelector(".custom-job-buttons");
    };

    // Function to add custom buttons
    const addCustomButtons = () => {
      console.log("[Jobs] Attempting to add custom buttons");
      const applyButtons = findApplyButtons();

      if (applyButtons.length === 0) {
        console.log("[Jobs] No apply buttons found, skipping");
        return;
      }

      console.log(`[Jobs] Processing ${applyButtons.length} apply buttons`);

      applyButtons.forEach((button) => {
        // Skip if this button already has our custom buttons
        if (hasCustomButtons(button)) {
          console.log("[Jobs] Button already has custom buttons, skipping");
          return;
        }
        console.log("[Jobs] Adding custom button to apply button:", button);

        // Try to extract job data
        const container =
          button.closest('[class*="detail"], [class*="job-description"]') ||
          (button.getRootNode() as Element);
        const jobData = extractJobData(container);

        if (!jobData) return;

        // Create container for our buttons
        const customButtonsContainer = document.createElement("div");
        customButtonsContainer.className = "custom-job-buttons";
        customButtonsContainer.style.display = "inline-flex";
        customButtonsContainer.style.gap = "4px";
        customButtonsContainer.style.marginLeft = "4px";
        customButtonsContainer.style.marginTop = "4px";
        // Create "Generate Cover Letter" button
        const generateCoverLetterButton = document.createElement("button");
        generateCoverLetterButton.className = "generate-cover-letter-button";
        generateCoverLetterButton.textContent =
          t("generate-cover-letter") || "Generate Cover Letter";
        generateCoverLetterButton.style.backgroundColor = "#fdc300";
        generateCoverLetterButton.style.color = "#ffffff";
        generateCoverLetterButton.style.padding = "12px 16px";
        generateCoverLetterButton.style.borderRadius = "6px";
        generateCoverLetterButton.style.borderColor = "#fdc300";
        generateCoverLetterButton.style.fontWeight = "500";
        generateCoverLetterButton.style.cursor = "pointer";
        generateCoverLetterButton.style.border = "1px solid #fdc300";
        generateCoverLetterButton.style.fontFamily = "inherit";
        generateCoverLetterButton.style.fontSize = "18px";

        generateCoverLetterButton.addEventListener("mouseover", () => {
          generateCoverLetterButton.style.backgroundColor = "#ffffff";
          generateCoverLetterButton.style.color = "#fdc300";
          generateCoverLetterButton.style.fontSize = "18px";
        });

        generateCoverLetterButton.addEventListener("mouseout", () => {
          generateCoverLetterButton.style.backgroundColor = "#fdc300";
          generateCoverLetterButton.style.color = "#ffffff";
          generateCoverLetterButton.style.fontSize = "18px";
        });

        generateCoverLetterButton.addEventListener("click", (e) => {
          e.stopPropagation();
          e.preventDefault();

          // Store the job data in localStorage to access it after navigation
          localStorage.setItem(
            "coverLetterJobAd",
            JSON.stringify({
              jobDescription: jobData.description,
              autoOpen: true,
            })
          );

          // Navigate to the cover letter generator page
          window.open("/profile/assessments/cover-letter-generator", "_blank");
        });

        // Add buttons to container
        customButtonsContainer.appendChild(generateCoverLetterButton);

        // Insert after the apply button
        if (button.parentNode) {
          button.parentNode.insertBefore(
            customButtonsContainer,
            button.nextSibling
          );
          console.log("[Jobs] Successfully added cover letter button!");
        } else {
          console.log("[Jobs] Failed to add button - no parent node");
        }
      });
    };

    // Set up a more efficient polling mechanism
    const setupPolling = () => {
      console.log("[Jobs] Setting up polling mechanism");

      // Initial attempts with shorter timeouts
      setTimeout(() => {
        console.log("[Jobs] Running first scheduled check (1.5s)");
        if (isComponentMounted) {
          addCustomButtons();
        }
      }, 1500);

      setTimeout(() => {
        console.log("[Jobs] Running second scheduled check (3s)");
        if (isComponentMounted) {
          addCustomButtons();
        }
      }, 3000);

      // Track how many polling attempts we've made
      let pollingCount = 0;
      const MAX_POLLING_ATTEMPTS = 10; // Limit polling to avoid resource drain

      // Then use a less frequent interval for checking
      const pollingInterval = setInterval(() => {
        pollingCount++;

        if (!isComponentMounted) {
          console.log("[Jobs] Component unmounted, stopping polling");
          clearInterval(pollingInterval);
          return;
        }

        if (pollingCount > MAX_POLLING_ATTEMPTS) {
          console.log("[Jobs] Reached max polling attempts, stopping polling");
          clearInterval(pollingInterval);
          return;
        }

        console.log(`[Jobs] Running polling check #${pollingCount}`);

        // Only continue polling if we don't find buttons
        const applyButtons = findApplyButtons();
        if (applyButtons.length === 0) {
          addCustomButtons();
        } else {
          // We found buttons, stop polling to save resources
          console.log(
            "[Jobs] Found apply buttons, stopping polling to save resources"
          );
          clearInterval(pollingInterval);
        }
      }, 5000);

      return () => {
        console.log("[Jobs] Cleaning up polling interval");
        clearInterval(pollingInterval);
      };
    };

    // Listen for jobads-job-board custom element loading events
    const waitForCustomElement = () => {
      console.log("[Jobs] Waiting for custom job-board element to load");

      // Monitor for changes in the shadow DOM of the job board
      const jobBoardElement = jobBoardRef.current;
      if (jobBoardElement && jobBoardElement.shadowRoot) {
        console.log("[Jobs] Job board already has shadow root, observing it");
        // If it already has a shadow root, observe it
        setupShadowObserver(jobBoardElement.shadowRoot);
      } else {
        console.log(
          "[Jobs] Job board doesn't have shadow root yet, waiting for it"
        );
        // Wait for the shadow root to be attached
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (
              mutation.type === "childList" &&
              mutation.addedNodes.length > 0
            ) {
              Array.from(mutation.addedNodes).forEach((node) => {
                if (node instanceof HTMLElement && node.shadowRoot) {
                  console.log("[Jobs] Found element with shadow root:", node);
                  setupShadowObserver(node.shadowRoot);
                }
              });
            }
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });

        return () => {
          console.log("[Jobs] Cleaning up custom element observer");
          observer.disconnect();
        };
      }
    };

    // Set up an observer for the shadow DOM to detect job listings
    const setupShadowObserver = (shadowRoot: ShadowRoot) => {
      console.log("[Jobs] Setting up shadow DOM observer");

      let shadowMutationCount = 0;
      const observer = new MutationObserver((mutations) => {
        shadowMutationCount++;

        // Log only occasionally to avoid console spam
        if (shadowMutationCount % 5 === 0) {
          console.log(
            `[Jobs] Shadow DOM mutation detected (count: ${shadowMutationCount}, mutations: ${mutations.length})`
          );
        }

        if (isComponentMounted) {
          addCustomButtons();
        }
      });

      observer.observe(shadowRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "hidden"],
      });

      console.log("[Jobs] Shadow DOM observer started");
    };

    // Set up events that might indicate job board interaction
    const setupEvents = () => {
      // Listen for clicks that could open job details
      document.addEventListener("click", () => {
        setTimeout(() => {
          if (isComponentMounted) {
            addCustomButtons();
          }
        }, 300);
      });

      // Listen for keyboard navigation (arrow keys, enter)
      document.addEventListener("keydown", (e) => {
        if (
          ["Enter", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(
            e.key
          )
        ) {
          setTimeout(() => {
            if (isComponentMounted) {
              addCustomButtons();
            }
          }, 300);
        }
      });
    };

    // Use a MutationObserver to detect changes to the DOM that might include job listings
    const observeDOMChanges = () => {
      let debounceTimeout: number | null = null;

      const observer = new MutationObserver(() => {
        // Clear any existing timeout
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }

        // Debounce to avoid too many runs
        debounceTimeout = window.setTimeout(() => {
          if (isComponentMounted) {
            addCustomButtons();
          }
        }, 300) as unknown as number;
      });

      // Observe the body for changes
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "hidden"],
      });

      return () => {
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }
        observer.disconnect();
      };
    };

    // Initialize all strategies
    const cleanup1 = observeDOMChanges();
    setupEvents();
    setupPolling();
    waitForCustomElement();

    // Try to detect when a job detail view is already open
    const checkForOpenJobDetails = () => {
      console.log("[Jobs] Setting up check for open job details");

      setTimeout(() => {
        if (!isComponentMounted) return;

        console.log("[Jobs] Checking for open job details");

        // Look for signs that a job detail view is open
        const detailViews = document.querySelectorAll(
          '[class*="detail"], [class*="job-description"], [class*="preview"]'
        );

        console.log(
          `[Jobs] Found ${detailViews.length} potential detail views`
        );

        if (detailViews.length > 0) {
          // We found what looks like an open job detail, try to add buttons
          console.log(
            "[Jobs] Found open job details, attempting to add buttons",
            detailViews
          );
          addCustomButtons();
        }
      }, 3000);
    };

    // Check if job details are already open
    checkForOpenJobDetails();

    return () => {
      isComponentMounted = false;
      cleanup1();
      // Other cleanup will be handled by their respective closure returns
    };
  }, [t, user, shouldRenderCoverLetter]);

  return (
    <div className="px-6 pt-4 lg:pt-9 sm:px-9 lg:px-0 flex flex-col w-full h-full items-center">
      <div className="w-full lg:w-5/6 max-w-screen-lg">
        <h1 className="text-Yellow-default text-lg font-semibold ">
          {t("page-title")}
        </h1>
        <div className="mb-4 md:mb-9 mt-1">
          {t("jobs-description").trim() || null}
        </div>

        {recommendedJobs && recommendedJobs.length > 0 && (
          <div className="mb-2">
            <RecommendationJobs
              jobsData={recommendedJobs}
              onJobSelect={handleJobSelect}
            />
          </div>
        )}

        <style>
          {`          
            .job-board-container {
              margin: 2rem 0;
              padding: 1rem;
              border: 1px solid rgb(25, 25, 217);
              border-radius: 4px;
              background-color: #f9f9f9;
              color: #F3F5F7;
            }
            
            jobads-job-board {
              --color-accent: #fdc300;
              --color-success: #202117;
              --color-error: #C41429;
            }
          `}
        </style>

        {selectedJobId && (
          <jobads-job-board
            ref={jobBoardRef}
            keywords={jobTitleParam}
            location={jobBoardLocation}
            apiKey="k24ST82TVyhPxBZy5fwQXXLQPK1mphqo"
            locale={locale}
            selectedSearchResultId={selectedJobId}
          />
        )}

        {!selectedJobId && (
          <jobads-job-board
            ref={jobBoardRef}
            keywords={jobTitleParam}
            location={jobBoardLocation}
            apiKey="k24ST82TVyhPxBZy5fwQXXLQPK1mphqo"
            locale={locale}
          />
        )}
      </div>
    </div>
  );
}
